import { identityFor, isLeagueActive } from '../../../data/leagues';
import { tierLabel } from '../../../lib/format';
import { NationalFlagEmblem } from '../NationalFlagEmblems';
import { cn } from '../../../lib/cn';
import { Check, ChevronRight, Plus } from '../icons';
import { useOrderedMemberships } from './helpers';
import type { LeagueSwitcherMenuProps } from './types';

export function LeagueSwitcherList({
  activeId,
  memberships,
  leagues,
  unreadByLeague,
  onSelect,
  onAll,
  compact = false
}: Omit<LeagueSwitcherMenuProps, 'onClose'> & { compact?: boolean }) {
  const ordered = useOrderedMemberships(memberships);

  return (
    <>
      {ordered.map((m) => {
        const id = identityFor(m.leagueId);
        const league = leagues?.find((l) => l.leagueId === m.leagueId);
        const name = league?.name ?? id?.name ?? m.leagueId;
        const active = isLeagueActive(m.leagueId);
        const current = m.leagueId === activeId;
        const unread = unreadByLeague[m.leagueId] ?? 0;
        return (
          <button
            key={m.leagueId}
            type="button"
            onClick={() => onSelect(m.leagueId)}
            aria-current={current ? 'true' : undefined}
            className={cn(
              'tap flex w-full items-center gap-3 text-left transition-colors',
              compact ? 'rounded-xl px-2.5 py-2' : 'rounded-[18px] px-3 py-[11px]',
              current
                ? 'bg-accent-wash ring-1 ring-inset ring-accent/25'
                : 'hover:bg-fill/70 active:bg-fill'
            )}
          >
            <NationalFlagEmblem
              leagueId={m.leagueId}
              country={league?.country ?? id?.country}
              slug={league?.cycling?.slug}
              size={compact ? 'sm' : 'md'}
              muted={!active}
            />
            <span className="min-w-0 flex-1">
              <span className={cn('block truncate font-bold tracking-[-0.01em] text-ink', compact ? 'text-[13px]' : 'text-[14.5px]')}>
                {name}
              </span>
              <span className="mt-0.5 flex items-center gap-1.5">
                <span className={cn('truncate font-medium text-ink2', compact ? 'text-[11px]' : 'text-[12px]')}>
                  {tierLabel(m.tier)}
                </span>
                {!active && (
                  <span className="shrink-0 rounded-full bg-gold-top/35 px-1.5 py-px text-[10px] font-bold uppercase tracking-wide text-gold-ink">
                    Waiting
                  </span>
                )}
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              {unread > 0 && (
                <span
                  className={cn(
                    'rounded-full bg-accent text-center font-bold text-white',
                    compact ? 'min-w-[16px] px-1 text-[9px] leading-[16px]' : 'min-w-[18px] px-1.5 text-[10px] leading-[18px]'
                  )}
                  aria-label={`${unread} unread`}
                >
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
              {current ? (
                <span
                  className={cn(
                    'grid place-items-center rounded-full bg-accent text-white',
                    compact
                      ? 'h-[22px] w-[22px] shadow-[0_3px_8px_-3px_rgba(10,154,114,0.65)]'
                      : 'h-[26px] w-[26px] shadow-[0_4px_10px_-3px_rgba(10,154,114,0.7)]'
                  )}
                >
                  <Check width={compact ? 13 : 15} height={compact ? 13 : 15} strokeWidth={3.2} />
                </span>
              ) : (
                <ChevronRight width={compact ? 16 : 18} height={compact ? 16 : 18} strokeWidth={2.4} className="text-ink4" />
              )}
            </span>
          </button>
        );
      })}

      <div className={cn('bg-black/[0.06]', compact ? 'my-1.5 h-px' : 'my-1 h-px')} />

      <button
        type="button"
        onClick={onAll}
        className={cn(
          'tap flex w-full items-center gap-3 text-left transition-colors hover:bg-fill/70 active:bg-fill',
          compact ? 'rounded-xl px-2.5 py-2' : 'rounded-[18px] px-3 py-[11px]'
        )}
      >
        <span
          className={cn(
            'grid shrink-0 place-items-center rounded-[10px] border border-dashed border-ink4 bg-white text-ink2',
            compact ? 'h-9 w-9' : 'h-11 w-11 rounded-[13px]'
          )}
        >
          <Plus width={compact ? 17 : 19} height={compact ? 17 : 19} strokeWidth={2.4} />
        </span>
        <span className="min-w-0 flex-1">
          <span className={cn('block font-bold tracking-[-0.01em] text-ink', compact ? 'text-[13px]' : 'text-[14.5px]')}>
            All competitions
          </span>
          <span className={cn('block font-medium text-ink2', compact ? 'text-[11px]' : 'text-[12px]')}>
            Browse or join another league
          </span>
        </span>
        <ChevronRight width={compact ? 16 : 18} height={compact ? 16 : 18} strokeWidth={2.4} className="text-ink4" />
      </button>
    </>
  );
}
